export const STRIPE_CHECKOUT_SESSION_STORAGE_KEY = "tint.billing.checkout.session_id"
export const STRIPE_CHECKOUT_OPERATION_STORAGE_KEY_PREFIX = "tint.billing.checkout.operation"

const CHECKOUT_SESSION_STORAGE_VERSION = 1
const CHECKOUT_SESSION_TTL_MS = 24 * 60 * 60 * 1_000
const CHECKOUT_OPERATION_STORAGE_VERSION = 1
const CHECKOUT_OPERATION_TTL_MS = 24 * 60 * 60 * 1_000

export type BillingCheckoutSessionOwner = {
  merchantId: string
  username: string
}

type StoredBillingCheckoutSession = {
  createdAt: number
  merchantId: string
  sessionId: string
  username: string
  v: typeof CHECKOUT_SESSION_STORAGE_VERSION
}

type StoredBillingCheckoutOperation = {
  createdAt: number
  merchantId: string
  operationId: string
  priceId: string
  username: string
  v: typeof CHECKOUT_OPERATION_STORAGE_VERSION
}

const normalize = (value: string | undefined | null) => value?.trim() || ""

const readSessionStorage = (key: string): string => {
  if (typeof window === "undefined") return ""

  try {
    return window.sessionStorage.getItem(key) ?? ""
  } catch {
    return ""
  }
}

const writeSessionStorage = (key: string, value: string): void => {
  if (typeof window === "undefined") return

  try {
    window.sessionStorage.setItem(key, value)
  } catch {
    // Checkout remains available when browser storage is blocked or full.
  }
}

const removeSessionStorage = (key: string): void => {
  if (typeof window === "undefined") return

  try {
    window.sessionStorage.removeItem(key)
  } catch {
    // Treat blocked storage as already empty.
  }
}

const hasOwner = (owner: BillingCheckoutSessionOwner) =>
  Boolean(normalize(owner.username) && normalize(owner.merchantId))

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === "object" && !Array.isArray(value))

const checkoutOperationStorageKey = (priceId: string, owner: BillingCheckoutSessionOwner) =>
  [
    STRIPE_CHECKOUT_OPERATION_STORAGE_KEY_PREFIX,
    normalize(owner.username),
    normalize(owner.merchantId),
    normalize(priceId),
  ]
    .map(encodeURIComponent)
    .join(":")

const parseStoredCheckoutSession = (raw: string): StoredBillingCheckoutSession | null => {
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!isRecord(parsed)) return null

    const createdAt = typeof parsed.createdAt === "number" ? parsed.createdAt : NaN
    const merchantId = typeof parsed.merchantId === "string" ? parsed.merchantId.trim() : ""
    const sessionId = typeof parsed.sessionId === "string" ? parsed.sessionId.trim() : ""
    const username = typeof parsed.username === "string" ? parsed.username.trim() : ""

    if (
      parsed.v !== CHECKOUT_SESSION_STORAGE_VERSION ||
      !Number.isFinite(createdAt) ||
      !merchantId ||
      !sessionId ||
      !username
    )
      return null

    return {
      createdAt,
      merchantId,
      sessionId,
      username,
      v: CHECKOUT_SESSION_STORAGE_VERSION,
    }
  } catch {
    return null
  }
}

const parseStoredCheckoutOperation = (raw: string): StoredBillingCheckoutOperation | null => {
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!isRecord(parsed)) return null

    const createdAt = typeof parsed.createdAt === "number" ? parsed.createdAt : NaN
    const merchantId = typeof parsed.merchantId === "string" ? parsed.merchantId.trim() : ""
    const operationId = typeof parsed.operationId === "string" ? parsed.operationId.trim() : ""
    const priceId = typeof parsed.priceId === "string" ? parsed.priceId.trim() : ""
    const username = typeof parsed.username === "string" ? parsed.username.trim() : ""

    if (
      parsed.v !== CHECKOUT_OPERATION_STORAGE_VERSION ||
      !Number.isFinite(createdAt) ||
      !merchantId ||
      !operationId ||
      !priceId ||
      !username
    )
      return null

    return {
      createdAt,
      merchantId,
      operationId,
      priceId,
      username,
      v: CHECKOUT_OPERATION_STORAGE_VERSION,
    }
  } catch {
    return null
  }
}

export const clearStoredCheckoutSessionId = () => {
  removeSessionStorage(STRIPE_CHECKOUT_SESSION_STORAGE_KEY)
}

export const readStoredCheckoutSessionId = (owner: BillingCheckoutSessionOwner) => {
  if (typeof window === "undefined") return ""

  const raw = readSessionStorage(STRIPE_CHECKOUT_SESSION_STORAGE_KEY).trim()
  if (!raw) return ""

  if (!hasOwner(owner)) return ""

  const stored = parseStoredCheckoutSession(raw)
  if (!stored) {
    clearStoredCheckoutSessionId()
    return ""
  }

  const isExpired = Date.now() - stored.createdAt > CHECKOUT_SESSION_TTL_MS
  const ownerMatches =
    stored.username === normalize(owner.username) &&
    stored.merchantId === normalize(owner.merchantId)

  if (isExpired || !ownerMatches) {
    clearStoredCheckoutSessionId()
    return ""
  }

  return stored.sessionId
}

export const persistCheckoutSessionId = (sessionId: string, owner: BillingCheckoutSessionOwner) => {
  if (typeof window === "undefined") return

  const normalizedSessionId = sessionId.trim()
  const normalizedOwner = {
    merchantId: normalize(owner.merchantId),
    username: normalize(owner.username),
  }

  if (!normalizedSessionId || !hasOwner(normalizedOwner)) return

  const payload: StoredBillingCheckoutSession = {
    createdAt: Date.now(),
    merchantId: normalizedOwner.merchantId,
    sessionId: normalizedSessionId,
    username: normalizedOwner.username,
    v: CHECKOUT_SESSION_STORAGE_VERSION,
  }

  writeSessionStorage(STRIPE_CHECKOUT_SESSION_STORAGE_KEY, JSON.stringify(payload))
}

export const clearStoredCheckoutOperationId = (
  operationId: string,
  priceId: string,
  owner: BillingCheckoutSessionOwner
) => {
  if (typeof window === "undefined") return

  const normalizedOperationId = normalize(operationId)
  const normalizedPriceId = normalize(priceId)
  if (!normalizedOperationId || !normalizedPriceId || !hasOwner(owner)) return

  const storageKey = checkoutOperationStorageKey(normalizedPriceId, owner)
  const raw = readSessionStorage(storageKey).trim()
  if (!raw) return

  const stored = parseStoredCheckoutOperation(raw)
  if (
    !stored ||
    (stored.operationId === normalizedOperationId &&
      stored.priceId === normalizedPriceId &&
      stored.username === normalize(owner.username) &&
      stored.merchantId === normalize(owner.merchantId))
  )
    removeSessionStorage(storageKey)
}

export const readStoredCheckoutOperationId = (
  priceId: string,
  owner: BillingCheckoutSessionOwner
) => {
  if (typeof window === "undefined") return ""

  const normalizedPriceId = normalize(priceId)
  if (!normalizedPriceId || !hasOwner(owner)) return ""

  const storageKey = checkoutOperationStorageKey(normalizedPriceId, owner)
  const raw = readSessionStorage(storageKey).trim()
  if (!raw) return ""

  const stored = parseStoredCheckoutOperation(raw)
  if (!stored) {
    removeSessionStorage(storageKey)
    return ""
  }

  const isExpired = Date.now() - stored.createdAt > CHECKOUT_OPERATION_TTL_MS
  const ownerMatches =
    stored.username === normalize(owner.username) &&
    stored.merchantId === normalize(owner.merchantId)

  if (isExpired || !ownerMatches || stored.priceId !== normalizedPriceId) {
    removeSessionStorage(storageKey)
    return ""
  }

  return stored.operationId
}

export const persistCheckoutOperationId = (
  operationId: string,
  priceId: string,
  owner: BillingCheckoutSessionOwner
) => {
  if (typeof window === "undefined") return

  const normalizedOperationId = normalize(operationId)
  const normalizedPriceId = normalize(priceId)
  const normalizedOwner = {
    merchantId: normalize(owner.merchantId),
    username: normalize(owner.username),
  }

  if (!normalizedOperationId || !normalizedPriceId || !hasOwner(normalizedOwner)) return

  const payload: StoredBillingCheckoutOperation = {
    createdAt: Date.now(),
    merchantId: normalizedOwner.merchantId,
    operationId: normalizedOperationId,
    priceId: normalizedPriceId,
    username: normalizedOwner.username,
    v: CHECKOUT_OPERATION_STORAGE_VERSION,
  }

  writeSessionStorage(
    checkoutOperationStorageKey(normalizedPriceId, normalizedOwner),
    JSON.stringify(payload)
  )
}
